import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TablePagination,
  Typography,
} from '@mui/material';
import { useTranslation } from '../../i18n';
import type { TranslationKey } from '../../i18n/useTranslation';
import SelectableGrid from '../file-browser/SelectableGrid';
import SharedEntryCard from './SharedEntryCard';
import {
  SHARED_PAGE_SIZE_OPTIONS,
  canClaimEmpty,
  counterpartyOptionName,
  emptyStateKey,
  entryHasMenu,
  groupByCounterparty,
  type CounterpartyOption,
  type SharedEntry,
  type SharedKind,
  type SharedSection,
} from './sharedViewModel';
import type { ShareCounterparties } from '../../types/api';

const HEADING_KEYS: Record<SharedSection['key'], TranslationKey> = {
  outbound: 'sharedView.outbound',
  inbound: 'sharedView.inbound',
  all: 'sharedView.allShares',
};

interface SectionPanelProps {
  section: SharedSection;
  kind: SharedKind;
  entries: SharedEntry[];
  /** Rows the SERVER returned, before any client-side direction filter. */
  rowCount: number;
  total: number;
  fetchSucceeded: boolean;
  counterparties?: ShareCounterparties;
  options: CounterpartyOption[];
  /** '' = no counterparty filter. */
  selectedCounterpartyId: string;
  onCounterpartyChange: (id: string) => void;
  /**
   * The revoked toggle, rendered only when BOTH are given — i.e. for the
   * flights listing. Reach has no revoked state, so the files panel offers
   * nothing here rather than a switch that changes nothing.
   */
  includeRevoked?: boolean;
  onIncludeRevokedChange?: (includeRevoked: boolean) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Entry keys of the view's selection — one selection across both sections. */
  selectedKeys: ReadonlySet<string>;
  onItemClick: (entry: SharedEntry, event: React.MouseEvent<HTMLElement>) => void;
  /** Click on empty grid space — clear the selection. */
  onBackgroundClick: () => void;
  /** Marquee over one counterparty's grid — replace the selection with what it covers. */
  onMarqueeSelect: (keys: string[]) => void;
  onOpen: (entry: SharedEntry) => void;
  onContextMenu: (event: React.MouseEvent<HTMLElement>, entry: SharedEntry) => void;
}

/**
 * One direction of the listing: a card with its own filters, the counterparty
 * grouping, and a pager over the server's result set as the card's footer.
 *
 * Presentational — the queries and all page/filter state live in the view, so
 * the two sections page independently without either owning a request.
 *
 * ### No count on a group header (#1498)
 *
 * The header used to carry "{n} item(s)" where n was the group's size ON THIS
 * PAGE. Paging is server-side, so with 90 outbound shares at 50 a page the
 * header read "Talentis 1" against six rows in the table, and the number
 * changed when you paged — a per-page figure dressed as a total. The truthful
 * numbers are already on screen: the pager's total for the section, and the
 * Organization filter for one counterparty. A server aggregate would be a
 * second source for a fact those two already state, so the count is gone
 * rather than corrected.
 */
const SharedSectionPanel: React.FC<SectionPanelProps> = ({
  section,
  kind,
  entries,
  rowCount,
  total,
  fetchSucceeded,
  counterparties,
  options,
  selectedCounterpartyId,
  onCounterpartyChange,
  includeRevoked,
  onIncludeRevokedChange,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  selectedKeys,
  onItemClick,
  onBackgroundClick,
  onMarqueeSelect,
  onOpen,
  onContextMenu,
}) => {
  const { t } = useTranslation();
  const groups = React.useMemo(() => groupByCounterparty(entries), [entries]);
  const showRevokedToggle = includeRevoked !== undefined && onIncludeRevokedChange !== undefined;

  return (
    <Paper variant="outlined" sx={{ mb: 2 }} data-testid={`shared-section-${section.key}`}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" component="h2">
          {t(HEADING_KEYS[section.key])}
        </Typography>
        {/* The filters, beside the listing they re-scope — and only that
            listing. The heading already states the direction, so the filter
            is labelled by what it selects rather than repeating "with"/"by". */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {showRevokedToggle && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeRevoked}
                  onChange={(e) => onIncludeRevokedChange(e.target.checked)}
                  inputProps={
                    {
                      'data-testid': `shared-include-revoked-${section.key}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={t('sharedView.includeRevoked')}
            />
          )}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id={`counterparty-filter-label-${section.key}`}>
              {t('sharedView.filterCounterparty')}
            </InputLabel>
            <Select
              labelId={`counterparty-filter-label-${section.key}`}
              label={t('sharedView.filterCounterparty')}
              value={selectedCounterpartyId}
              onChange={(e) => onCounterpartyChange(e.target.value)}
              data-testid={`shared-counterparty-filter-${section.key}`}
            >
              <MenuItem value="">{t('sharedView.allCounterparties')}</MenuItem>
              {options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {/* Never a guess: an org the payload could not name is labelled
                      neutrally rather than borrowed from another row. */}
                  {counterpartyOptionName(option, counterparties) ?? t('sharedView.unknownOrg')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* An empty panel is a claim about the data, so it is only made when the
          fetch succeeded — and it is measured against what the SERVER
          returned, not against `entries`, which the direction filter above can
          legitimately empty. On failure the list is also empty, and saying
          nothing is shared there would state an unknown as a fact. */}
      {canClaimEmpty(fetchSucceeded, rowCount) ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2, py: 2 }}
          data-testid={`shared-empty-${section.key}`}
        >
          {t(emptyStateKey(section.key, kind, selectedCounterpartyId !== ''))}
        </Typography>
      ) : null}

      {groups.map((group) => (
        <Box key={group.organizationId} sx={{ pt: 1.5 }} data-testid="shared-counterparty-group">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, px: 2 }}>
            {group.organizationName ?? t('sharedView.unknownOrg')}
          </Typography>
          {/* The Files browser's grid: marquee on empty space, click there to
              clear (#1692). One per counterparty group, so a marquee selects
              within the group it was drawn in. */}
          <SelectableGrid
            data-testid="shared-entry-grid"
            onBackgroundClick={onBackgroundClick}
            onMarqueeSelect={onMarqueeSelect}
          >
            {group.entries.map((entry) => (
              <SharedEntryCard
                key={entry.key}
                entry={entry}
                selected={selectedKeys.has(entry.key)}
                hasMenu={entryHasMenu(entry, section)}
                onClick={onItemClick}
                onOpen={onOpen}
                onContextMenu={onContextMenu}
              />
            ))}
          </SelectableGrid>
        </Box>
      ))}

      {/* The card's footer, not a table's: same divider as the header above,
          so the "1-50 of 90" row reads as the grid's own pager. */}
      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => onPageChange(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={SHARED_PAGE_SIZE_OPTIONS}
          labelRowsPerPage={t('sharedView.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            t('sharedView.paginationRange', { from, to, count })
          }
          sx={{ borderTop: 1, borderColor: 'divider' }}
          data-testid={`shared-pagination-${section.key}`}
        />
      )}
    </Paper>
  );
};

export default SharedSectionPanel;
